import { QueryObserver } from "@tanstack/react-query";
import type { QueryClient, QueryFunction, QueryKey } from "@tanstack/react-query";

export type RunOperatorQueryParams<TData> = {
  queryClient: QueryClient;
  queryKey: QueryKey;
  queryFn: QueryFunction<TData>;
};

/**
 * Runs a React Query query imperatively and resolves with its data, for the generated
 * pack fetchers (`use<Operation>OperatorQuery`). `useDashboardApiOperatorQuery` is the
 * same idea for the dashboard API registry.
 *
 * A `QueryObserver` rather than `queryClient.fetchQuery`, so the observer count stays
 * above zero and React Query cannot cancel a shared in-flight fetch. Raises no error
 * modal: a failed Operator tool call reports through the tool.
 */
export const runOperatorQuery = <TData>({
  queryClient,
  queryKey,
  queryFn,
}: RunOperatorQueryParams<TData>): Promise<TData> => {
  const observer = new QueryObserver<TData, unknown, TData, TData, QueryKey>(queryClient, {
    queryKey,
    queryFn,
    retry: false,
  });

  return new Promise<TData>((resolve, reject) => {
    let settled = false;
    let unsubscribe: (() => void) | undefined;

    const settle = (callback: () => void) => {
      if (settled) {
        return;
      }
      settled = true;
      unsubscribe?.();
      callback();
    };

    const handleResult = (result: ReturnType<typeof observer.getCurrentResult>) => {
      // A refetch keeps the previous error until the new request settles.
      if (result.isFetching) {
        return;
      }
      if (result.status === "error") {
        settle(() => reject(result.error));
      } else if (result.status === "success") {
        settle(() => resolve(result.data));
      }
    };

    unsubscribe = observer.subscribe(handleResult);
    // Fresh data notifies no new subscriber, so seed from the current result.
    handleResult(observer.getCurrentResult());
    if (settled) {
      unsubscribe();
    }
  });
};
